import { useViewModel } from "@lumora/ui";
import { PackageCatalog, type PackageId } from "../models/PackageCatalog.js";
import { BUDGETS, CATEGORIES } from "../models/QuoteRequest.js";
import type { QuoteViewModel } from "../viewmodels/QuoteViewModel.js";
import { QuoteTabs } from "./QuoteTabs.js";

const catalog = PackageCatalog.launch();

/** Dumb View: renders QuoteViewModel state, forwards input to its commands. */
export function QuotePage({ viewModel }: { viewModel: QuoteViewModel }) {
  const state = useViewModel(viewModel);

  if (state.submitted) {
    return (
      <section className="site-contact t-quote" data-page="quote">
        <h1>Thanks, request received</h1>
        <p role="status">We will reply by email with a fixed quote and next steps.</p>
      </section>
    );
  }

  return (
    <section className="site-contact t-quote" data-page="quote">
      <p className="t-kicker">Quote</p>
      <h1>Request a quote</h1>
      <QuoteTabs active="quote" />
      <p className="t-section-lead">Takes about a minute. No payment is taken on this form.</p>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void viewModel.submit();
        }}
      >
        <label>
          <span>Name</span>
          <input type="text" autoComplete="name" value={state.name} disabled={state.submitting} onChange={(e) => viewModel.setName(e.target.value)} />
        </label>
        <label>
          <span>Email</span>
          <input type="email" autoComplete="email" value={state.email} disabled={state.submitting} onChange={(e) => viewModel.setEmail(e.target.value)} />
        </label>
        <label>
          <span>Product category</span>
          <select value={state.category} disabled={state.submitting} onChange={(e) => viewModel.setCategory(e.target.value)}>
            <option value="">Choose one</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Budget</span>
          <select value={state.budget} disabled={state.submitting} onChange={(e) => viewModel.setBudget(e.target.value)}>
            <option value="">Choose one</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Package</span>
          <select value={state.packageId} disabled={state.submitting} onChange={(e) => viewModel.setPackage(e.target.value as PackageId | "")}>
            <option value="">Not sure yet</option>
            {catalog.all().map((p) => (
              <option key={p.id} value={p.id}>{`${p.name} (${p.priceLabel()})`}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Number of products</span>
          <input type="number" min={1} inputMode="numeric" value={state.skuCount} disabled={state.submitting} onChange={(e) => viewModel.setSkuCount(Number(e.target.value))} />
        </label>
        {state.estimateUsd !== null ? (
          <p className="t-note" data-quote-estimate>Launch-price estimate: ${state.estimateUsd}</p>
        ) : null}
        <label>
          <span>About the product</span>
          <textarea rows={5} value={state.details} disabled={state.submitting} onChange={(e) => viewModel.setDetails(e.target.value)} placeholder="Size, materials, where the images will be used, any links or references." />
        </label>
        {state.errors.length > 0 ? (
          <ul data-lumora-errors role="alert">
            {state.errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        ) : null}
        <button type="submit" disabled={state.submitting}>
          {state.submitting ? "Sending..." : "Send request"}
        </button>
      </form>
    </section>
  );
}
